import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { LocationWizardData } from '../types';
import { StepErrors } from '../stepValidation';
import WizardRequiredNote from '../components/WizardRequiredNote';
import { toClampedNumber, toNullableNumber } from '../utility';
import AutofillTextField from '../components/AutofillTextField';

interface LocationSettingsStepProps {
  data: LocationWizardData;
  updateData: (partial: Partial<LocationWizardData>) => void;
  errors?: StepErrors;
}

const DESCRIPTION_PLACEHOLDER =
  'Describe what makes your space ideal for parking or storage. Highlight key features like security, hours, location perks or equipment access. Buyers want to know why they should choose your site.';

const INSTRUCTIONS_PLACEHOLDER =
  'Explain how buyers access your facility \u2013 gate codes, entry points, hours or any special instructions. Clear directions build trust and help avoid confusion at check-in.';

const GATE_HOURS_PLACEHOLDER = 'e.g. Mon\u2013Fri 7am\u20136pm';

const AFTER_HOURS_PLACEHOLDER = 'e.g. Call dispatch at 555-0100';

/**
 * Step 2 (Location Settings) of the Create/Edit Location wizard.
 *
 * Collects: description, facility instructions, 24/7 access toggle,
 * gate hours and after-hours access procedure (when not 24/7),
 * minimum number of spaces, and location capacity.
 */
const LocationSettingsStep = (props: LocationSettingsStepProps) => {
  const { data, updateData, errors } = props;
  const fieldErrors = errors || {};

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Settings
      </Typography>
      <WizardRequiredNote />

      <AutofillTextField
        autofillId="location-settings-description"
        label="Location Description"
        placeholder={DESCRIPTION_PLACEHOLDER}
        multiline
        minRows={4}
        maxRows={12}
        required
        fullWidth
        value={data.locationDescription}
        onChange={(e) => updateData({ locationDescription: e.target.value })}
        error={Boolean(fieldErrors.locationDescription)}
        helperText={fieldErrors.locationDescription}
        sx={{ mb: 3 }}
      />

      <TextField
        label="Facility Instructions"
        placeholder={INSTRUCTIONS_PLACEHOLDER}
        multiline
        minRows={3}
        fullWidth
        value={data.instructions}
        onChange={(e) => updateData({ instructions: e.target.value })}
        sx={{ mb: 3 }}
      />

      <FormControl sx={{ mb: 2, display: 'block' }}>
        <FormLabel id="wizard-247-access-label">Do you provide 24/7 Access?</FormLabel>
        <RadioGroup
          row
          aria-labelledby="wizard-247-access-label"
          value={data.has247Access ? 'yes' : 'no'}
          onChange={(e) => updateData({ has247Access: e.target.value === 'yes' })}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      {!data.has247Access && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
            mb: 4,
          }}
        >
          <Box>
            <TextField
              label="Gate Hours"
              placeholder={GATE_HOURS_PLACEHOLDER}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-describedby': 'wizard-gate-hours-hint' }}
              value={data.gateHours}
              onChange={(e) => updateData({ gateHours: e.target.value })}
            />
            <Typography
              id="wizard-gate-hours-hint"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              Hours your gate is staffed or accessible to buyers.
            </Typography>
          </Box>

          <Box>
            <TextField
              label="After-Hours Access Procedure"
              placeholder={AFTER_HOURS_PLACEHOLDER}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'aria-describedby': 'wizard-after-hours-hint' }}
              value={data.afterHoursAccessProcedure}
              onChange={(e) => updateData({ afterHoursAccessProcedure: e.target.value })}
            />
            <Typography
              id="wizard-after-hours-hint"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              Instructions for buyers needing access outside gate hours.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <Box>
          <AutofillTextField
            autofillId="location-settings-min-spaces"
            label="Minimum Number of Spaces"
            type="number"
            fullWidth
            inputProps={{ min: 1, 'aria-describedby': 'wizard-min-spaces-hint' }}
            value={data.minNumberOfSpaces ?? ''}
            onChange={(e) => updateData({ minNumberOfSpaces: toNullableNumber(e.currentTarget) })}
          />
          <Typography
            id="wizard-min-spaces-hint"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, ml: 1.75, fontWeight: 700 }}
          >
            The least number of spaces a buyer can reserve in a single booking.
          </Typography>
        </Box>

        <Box>
          <AutofillTextField
            autofillId="location-settings-capacity"
            label="Location Capacity"
            type="number"
            required
            fullWidth
            inputProps={{
              min: 1,
              ...(fieldErrors.totalNumberOfSpaces
                ? {}
                : { 'aria-describedby': 'wizard-capacity-hint' }),
            }}
            value={data.totalNumberOfSpaces ?? ''}
            onChange={(e) => updateData({ totalNumberOfSpaces: toClampedNumber(e.currentTarget, 1) })}
            error={Boolean(fieldErrors.totalNumberOfSpaces)}
            helperText={fieldErrors.totalNumberOfSpaces || undefined}
          />
          {!fieldErrors.totalNumberOfSpaces && (
            <Typography
              id="wizard-capacity-hint"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              Enter the <strong>Total Yard Capacity</strong> — this should reflect all spaces at your
              facility, both currently available and unavailable.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LocationSettingsStep;
